import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Add, ArrowBack, VisibilityOutlined } from "@material-ui/icons";
import { connect } from "react-redux";
import HeaderDescription from "../../../components/commons/HeaderDescription";
import MOButtonV2 from "../../../components/commons/MOButtonV2";
import SearchingInput from "../../../components/commons/SearchingInput";
import DropDown from "../../../components/commons/DropDown";
import Pagination from "../../../components/commons/Pagination";
import DeleteModal from "../../../components/commons/Modals/DeleteModal";
import HorizontalIcon from "../../../assets/icons/horizontalIcon.png";
import EditIcon from "../../../assets/icons/editUpdateIcon.png";
import DeleteIcon from "../../../assets/icons/menuDelete.png";
import mobioApi from "../../../services";
import { toastMessage } from "../../../helpers/toastMessage";
import {
  getSourceTypeLabel,
  getStatusClassName,
  getStatusLabel,
} from "./knowledgeDocuments";
import { formatDocumentDate } from "./knowledgeDocumentUtils";

const ITEMS_PER_PAGE = 5;

const UploadDocuments = ({ token }) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState("");
  const [documents, setDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchValue.trim());
      setCurrentPage(1);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  const fetchDocuments = useCallback(
    async (page = 1, limit = ITEMS_PER_PAGE) => {
      try {
        setLoading(true);
        const api = mobioApi(token);
        const response = await api.listDocuments(page, limit, searchQuery);
        const list = response?.data?.data || [];
        const pagination = response?.data?.pagination || {};
        const total = pagination.total ?? 0;

        return {
          items: list,
          total,
        };
      } catch (error) {
        toastMessage(
          "error",
          error?.response?.data?.msg || "Documents could not be loaded."
        );
        return { items: [], total: 0 };
      } finally {
        setLoading(false);
      }
    },
    [token, searchQuery, listRefreshKey]
  );

  const handlePageChange = useCallback((items) => {
    setDocuments(items || []);
  }, []);

  const handleDeleteDocument = async () => {
    if (!deletingDocument?.id) {
      return;
    }

    try {
      const api = mobioApi(token);
      const response = await api.deleteDocument(deletingDocument.id);
      toastMessage("success", response?.data?.msg || "Document deleted.");
      const isLastItemOnPage = documents.length === 1;
      const nextPage =
        isLastItemOnPage && currentPage > 1 ? currentPage - 1 : currentPage;
      setDeleteModalOpen(false);
      setDeletingDocument(null);
      setCurrentPage(nextPage);
      setListRefreshKey((key) => key + 1);
    } catch (error) {
      toastMessage(
        "error",
        error?.response?.data?.msg || "This document could not be deleted."
      );
    }
  };

  return (
    <div className="ai-area-page">
      <button
        type="button"
        className="ai-area-back-btn"
        onClick={() => navigate("/dashboard/ai-area")}
      >
        <ArrowBack className="ai-area-back-icon" />
        Back to AI Integration
      </button>

      <div className="ai-area-page-header">
        <HeaderDescription
          heading="Upload Documents"
          description="Add and manage PDF files the AI can read. You can open a document to view it or update it when something changes."
        />

        <div className="ai-area-page-header-actions">
          <SearchingInput
            className="ai-area-search"
            crossIcon
            onSearching={setSearchValue}
          />
          <MOButtonV2
            text="Upload PDF"
            textColor="#ffffff"
            radius={4}
            backgroundColor="#29AEE6"
            height={36}
            fontSize={13}
            padding="8px 16px"
            IconType={() => <Add style={{ marginRight: 8, fontSize: 16 }} />}
            onClick={() =>
              navigate("/dashboard/ai-area/upload-documents/add")
            }
          />
        </div>
      </div>

      <div className="bg-white mt-20 border-radius-8">
        <table className="custom-table-v3">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Status</th>
              <th>Version</th>
              <th>Updated</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6">
                  <div className="table-no-record-section">Loading...</div>
                </td>
              </tr>
            ) : documents.length ? (
              documents.map((row) => (
                <tr key={row.id}>
                  <td className="ai-area-table-title">{row.title}</td>
                  <td>{getSourceTypeLabel(row.source_type)}</td>
                  <td>
                    <span
                      className={`ai-area-status-badge ai-area-status-badge-${getStatusClassName(
                        row.status
                      )}`}
                    >
                      {getStatusLabel(row.status)}
                    </span>
                  </td>
                  <td>{row.version}</td>
                  <td>{formatDocumentDate(row.updated_at)}</td>
                  <td>
                    <DropDown
                      dropDownOpen={anchorEl}
                      setDropDownOpen={setAnchorEl}
                      uniqueId={row.id}
                      IconRender={() => <img src={HorizontalIcon} alt="" />}
                      dropDownShape="horizontal"
                      menu={[
                        {
                          onClick: () =>
                            navigate(
                              `/dashboard/ai-area/upload-documents/view/${row.id}`
                            ),
                          IconRender: () => (
                            <VisibilityOutlined
                              style={{ fontSize: 16, color: "#777e90" }}
                            />
                          ),
                          title: "View",
                        },
                        {
                          onClick: () =>
                            navigate(
                              `/dashboard/ai-area/upload-documents/edit/${row.id}`
                            ),
                          IconRender: () => <img src={EditIcon} alt="" />,
                          title: "Edit",
                        },
                        {
                          onClick: () => {
                            setDeletingDocument(row);
                            setDeleteModalOpen(true);
                          },
                          IconRender: () => <img src={DeleteIcon} alt="" />,
                          title: "Delete",
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">
                  <div className="table-no-record-section">
                    No documents are found.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="ai-area-pagination">
          <Pagination
            fetchData={fetchDocuments}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            onCurrentPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      <DeleteModal
        title="Delete Document"
        heading="Are You Sure?"
        description="This document will be permanently deleted. The AI will no longer be able to use it."
        modalOpen={deleteModalOpen}
        setModalOpen={setDeleteModalOpen}
        apiCall={handleDeleteDocument}
      />
    </div>
  );
};

const mapState = (state) => ({
  token: state.userInfo.token,
});

export default connect(mapState)(UploadDocuments);
